import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Checkbox, Divider, Stack, Typography, Alert } from '@mui/material';
import { authService, roleService, peopleService } from '../../services/api';
import { usePermissions } from '../PermissionsContext';
import { useToast } from '../Common/ToastProvider';

export default function AppAccountTab({ person, summary: summaryProp }){
  const toast = useToast();
  const personId = person?.Id ?? person?.id;
  const [summary, setSummary] = useState(summaryProp || null); // people profile-summary
  const [allRoles, setAllRoles] = useState([]);
  const [userRoleIds, setUserRoleIds] = useState([]); // server state
  const [draftRoleIds, setDraftRoleIds] = useState([]); // UI draft
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const { isSystemAdmin, roles } = usePermissions();

  const userId = (summary?.appUser?.Id || summary?.appUser?.id || summary?.AppUser?.Id || summary?.AppUser?.id);
  const isLinked = !!userId;

  useEffect(()=>{
    let cancelled = false;
    (async ()=>{
      if(!personId) return;
      setLoading(true);
      try{
        // Prefer parent-provided summary, fallback to fetch
        const prof = summaryProp || await peopleService.getProfileSummary(personId);
        if(cancelled) return;
        setSummary(prof);
        const appUser = prof?.appUser || prof?.AppUser || prof?.user || prof?.User;
        if(appUser?.Id || appUser?.id){
          const id = appUser.Id || appUser.id;
          const [rolesResp, allResp] = await Promise.all([
            roleService.getUserRoles(id),
            roleService.getAll(),
          ]);
          if(cancelled) return;
          setAllRoles(allResp || []);
          const currentRoleIds = (rolesResp||[]).map(r=> r.id || r.Id);
          setUserRoleIds(currentRoleIds);
          setDraftRoleIds(currentRoleIds);
        } else {
          const allResp = await roleService.getAll();
          if(cancelled) return;
          setAllRoles(allResp || []);
          setUserRoleIds([]);
          setDraftRoleIds([]);
        }
      } finally {
        if(!cancelled) setLoading(false);
      }
    })();
    return ()=>{ cancelled = true };
  }, [personId, summaryProp]);

  const byName = useMemo(()=>{
    const map = {};
    (allRoles||[]).forEach(r=>{ map[(r.name||r.Name)] = (r.id||r.Id); });
    return map;
  }, [allRoles]);

  const isSelfAndSysAdmin = useMemo(()=>{
    try{
      const profile = window.__lastProfile; // optional cache if present
      const selfUserId = profile?.id;
      return (isSystemAdmin && userId && selfUserId && Number(selfUserId)===Number(userId));
    }catch{return false;}
  }, [isSystemAdmin, userId]);

  const toggleRole = (roleId, checked)=>{
    setDraftRoleIds(prev=>{
      const set = new Set(prev);
      if(checked) set.add(roleId); else set.delete(roleId);
      return Array.from(set);
    });
  };

  const save = async ()=>{
    if(!userId) return;
    // Guard: prevent removing own SystemAdmin
    if(isSelfAndSysAdmin){
      const sysAdminId = byName['SystemAdmin'];
      if(sysAdminId && !draftRoleIds.includes(sysAdminId)){
        toast("You can't remove your own SystemAdmin role.", { variant: 'error' });
        return;
      }
    }
    const toAssign = draftRoleIds.filter(id => !userRoleIds.includes(id));
    const toRevoke = userRoleIds.filter(id => !draftRoleIds.includes(id));
    setSaving(true);
    try{
      for(const rid of toAssign){ await roleService.assignToUser(rid, userId); }
      for(const rid of toRevoke){ await roleService.revokeFromUser(rid, userId); }
      setUserRoleIds(draftRoleIds);
      window.dispatchEvent(new CustomEvent('hrapp.toast', { detail: { sev:'success', msg:'Roles updated' } }));
    }catch(e){
      console.error(e);
      window.dispatchEvent(new CustomEvent('hrapp.toast', { detail: { sev:'error', msg: e?.response?.data || 'Грешка при запис на роли' } }));
    }finally{
      setSaving(false);
    }
  };

  const discard = ()=>{ setDraftRoleIds(userRoleIds); };

  if(!personId) return <Typography variant="body2" color="text.secondary">Select a person first.</Typography>;
  if(loading) return <Typography variant="body2" color="text.secondary">Loading...</Typography>;

  if(!isLinked){
    return (
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="subtitle2" sx={{ mb:1 }}>App Account</Typography>
          <Button size="small" onClick={async()=>{
            // Force refresh of summary
            if(personId){
              peopleService.invalidateProfileSummary(personId);
              const s = await peopleService.getProfileSummary(personId);
              setSummary(s);
            }
          }}>Refresh</Button>
        </Stack>
        <Alert severity="info">No App Account for this person. Link an Entra account in the Identity tab.</Alert>
      </Box>
    );
  }

  // CEV-S3 — access ends automatically on the leaving date
  const leftOnIso = summary?.employee?.terminationDate || summary?.employee?.TerminationDate || null;
  const accountActive = (summary?.appUser?.isActive ?? summary?.appUser?.IsActive) !== false;
  const leftOn = leftOnIso ? new Date(leftOnIso) : null;
  const leftOnText = leftOn ? leftOn.toLocaleDateString('bg-BG') : '';
  const hasLeft = leftOn ? leftOn <= new Date() : false;

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb:1 }}>App Account</Typography>
      {leftOn && (hasLeft || !accountActive) && (
        <Alert severity="warning" sx={{ mb:2 }} data-testid="access-ended-note">
          Access ended on {leftOnText} (leaving date). Sign-in does not create a new account.
        </Alert>
      )}
      {leftOn && !hasLeft && accountActive && (
        <Alert severity="info" sx={{ mb:2 }} data-testid="access-ends-note">
          Access ends automatically on {leftOnText} (leaving date).
        </Alert>
      )}
      <Box sx={{ mb:2 }}>
        <Typography variant="body2"><b>UserId:</b> {userId}</Typography>
        <Typography variant="body2"><b>Email:</b> {summary?.appUser?.Email || summary?.appUser?.email}</Typography>
        <Typography variant="body2"><b>Display Name:</b> {summary?.appUser?.DisplayName || summary?.appUser?.displayName}</Typography>
        <Typography variant="body2"><b>Azure AD ObjectId:</b> {summary?.appUser?.AzureAdObjectId || summary?.appUser?.azureAdObjectId || '-'}</Typography>
      </Box>

      <Alert severity="info" sx={{ mb:2 }}>
        <Typography variant="body2" sx={{ mb:0.5 }}>
          Управление на роли в приложението. Промените се буферират и се записват с Save; Discard връща последното записано състояние.
        </Typography>
        <Typography variant="body2" sx={{ mb:0.5 }}>
          Одобрения на отпуски:
        </Typography>
        <ul style={{ marginTop: 0, marginBottom: 8 }}>
          <li><Typography variant="body2">Department ниво: необходима е роля <b>DepartmentManager</b> + да сте посочен като <b>Manager</b> в съответния департамент.</Typography></li>
          <li><Typography variant="body2">Board ниво: необходима е роля <b>BoardManager</b> или <b>HRManager</b> (независимо от департаменти).</Typography></li>
        </ul>
        <Typography variant="body2">
          Забележка: Не можете да премахнете собствената си роля <b>SystemAdmin</b>.
        </Typography>
      </Alert>

      <Divider sx={{ my:1 }} />
      <Typography variant="subtitle2" sx={{ mb:1 }}>Roles</Typography>
      <Stack spacing={0.5}>
        {(allRoles||[]).map(r=>{
          const rid = r.id || r.Id; const name = r.name || r.Name;
          const checked = draftRoleIds.includes(rid);
          const disabled = isSelfAndSysAdmin && name==='SystemAdmin' ? true : false;
          return (
            <Stack key={rid} direction="row" spacing={1} alignItems="center">
              <Checkbox size="small" checked={checked} disabled={disabled} onChange={(e)=> toggleRole(rid, e.target.checked)} />
              <Typography variant="body2" sx={{ minWidth:160 }}>{name}</Typography>
              <Typography variant="caption" color="text.secondary">{r.description || r.Description}</Typography>
            </Stack>
          );
        })}
      </Stack>

      <Stack direction="row" spacing={1} sx={{ mt:2 }}>
        <Button variant="contained" size="small" onClick={save} disabled={saving}>Save</Button>
        <Button variant="text" size="small" onClick={discard} disabled={saving}>Discard</Button>
      </Stack>
    </Box>
  );
}

